import {createFileRoute, Outlet} from "@tanstack/react-router";
import {
    getProducts,
    listProductBrands,
    listProductCategories,
    listProductTypes,
    listVariants,
    type Product,
    type ProductBrand,
    type ProductCategory,
    type ProductType,
    type Variant,
} from "~/domainModels/ottokart/products";
import type {Quotation} from "~/domainModels/ottokart/quotations";
import {QuotationStatus} from "~/utilities/ottokart/quotationConstants";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/advanced-quotations/create-quotation")({
    loader: async ({context}) => {
        const {intellsysBaseUrl} = context;
        const workspaceId = context.workspace.id;
        const accessToken = context.authenticationStatus.accessToken;

        const [variantsResult, productsResult, productTypesResult, productBrandsResult, productCategoriesResult] =
            await Promise.all([
                listVariants({intellsysBaseUrl, workspaceId, accessToken}),
                getProducts({intellsysBaseUrl, workspaceId, accessToken}),
                listProductTypes({intellsysBaseUrl, workspaceId, accessToken}),
                listProductBrands({intellsysBaseUrl, workspaceId, accessToken}),
                listProductCategories({intellsysBaseUrl, workspaceId, accessToken}),
            ]);

        if (variantsResult.success !== true) throw variantsResult.error;
        if (productsResult.success !== true) throw productsResult.error;
        if (productTypesResult.success !== true) throw productTypesResult.error;
        if (productBrandsResult.success !== true) throw productBrandsResult.error;
        if (productCategoriesResult.success !== true) throw productCategoriesResult.error;

        const blankQuotation = {
            id: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: "",
            updatedBy: "",
            slug: "",
            customerId: "",
            status: QuotationStatus.Requested,
            discountType: null,
            discountValue: null,
            taxRate: 18,
            billingDetailsCompanyName: "",
            billingDetailsGstin: null,
            billingDetailsPhoneNumber: "",
            billingDetailsEmail: "",
            billingDetailsAddress: "",
            billingDetailsState: "",
            billingDetailsCity: "",
            billingDetailsPinCode: "",
            shippingDetailsCompanyName: "",
            shippingDetailsGstin: null,
            shippingDetailsPhoneNumber: "",
            shippingDetailsEmail: "",
            shippingDetailsAddress: "",
            shippingDetailsState: "",
            shippingDetailsCity: "",
            shippingDetailsPinCode: "",
            isBillingSameAsShipping: false,
            sourceQuotationId: "00000000-0000-0000-0000-000000000000",
            termsAndConditions: [],
            contactNumber: "",
            emailId: "",
            incoTerms: "",
            termsOfPayment: "",
            customerReferenceNumber: "",
            customerReferenceDate: "",
            consigneeShipTo: "",
            buyerBillTo: "",
            freightCharges: null,
            items: [],
            categoryName: "",
        } as unknown as Quotation;

        const variantsById = new Map<string, Variant>(variantsResult.data.map((variant) => [variant.id, variant]));
        const productsById = new Map<string, Product>(productsResult.data.map((product) => [product.id, product]));
        const productTypesById = new Map<string, ProductType>(productTypesResult.data.map((type) => [type.id, type]));
        const productBrandsById = new Map<string, ProductBrand>(productBrandsResult.data.map((brand) => [brand.id, brand]));
        const productCategoriesById = new Map<string, ProductCategory>(productCategoriesResult.data.map((category) => [category.id, category]));
        return {quotation: blankQuotation, variantsById, productsById, productTypesById, productBrandsById, productCategoriesById};
    },
    component: Outlet,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
